import type { SiteExperience, SiteId } from "@mfa/v5-contracts";
import {
  resolveSiteExperience as resolveSharedSiteExperience,
  SITE_EXPERIENCES,
  siteExperience,
} from "@mfa/v5-contracts";

export type { SiteExperience } from "@mfa/v5-contracts";
export { SITE_EXPERIENCES } from "@mfa/v5-contracts";

/** What the server publishes at /runtime-config.js (window.__MFA_V5_CONFIG__). */
export interface PublicRuntimeConfig {
  siteId?: SiteId;
  apiBaseUrl?: string;
  firebase?: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    appId?: string;
  };
  /**
   * Present (true) only when the server runs with DEV_TOOLS_ENABLED: mounts the Dev Tools
   * dashboard at /dev/* (the RPC explorer is its /dev/rpc tab) in both route trees. Absent
   * in prod, where the server refuses the flag, so every /dev path is the real not-found
   * page there.
   */
  devTools?: boolean;
}

declare global {
  interface Window {
    __MFA_V5_CONFIG__?: PublicRuntimeConfig;
  }
}

function isDevelopmentHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost")
  );
}

export function resolveSiteExperience(
  hostname: string,
  runtimeConfig: PublicRuntimeConfig = {},
  environmentSiteId?: string,
): SiteExperience {
  if (runtimeConfig.siteId) return siteExperience(runtimeConfig.siteId);

  const matched = resolveSharedSiteExperience(hostname);
  if (matched) return matched;

  if (
    isDevelopmentHost(hostname) &&
    (environmentSiteId === "myfirstagent" || environmentSiteId === "launchyouragent")
  ) {
    return SITE_EXPERIENCES[environmentSiteId];
  }

  throw new Error(`UNRECOGNIZED_SITE_HOST:${hostname}`);
}

// The injected server config wins over the VITE_* fallbacks (dev without the proxy).
export function getRuntimeConfig(): PublicRuntimeConfig {
  const injected = window.__MFA_V5_CONFIG__ ?? {};
  const environmentSiteId = import.meta.env.VITE_SITE_ID;
  const siteId =
    environmentSiteId === "myfirstagent" || environmentSiteId === "launchyouragent"
      ? environmentSiteId
      : undefined;
  const firebase =
    import.meta.env.VITE_FIREBASE_API_KEY && import.meta.env.VITE_FIREBASE_PROJECT_ID
      ? {
          apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
          authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
          ...(import.meta.env.VITE_FIREBASE_APP_ID
            ? { appId: import.meta.env.VITE_FIREBASE_APP_ID }
            : {}),
        }
      : undefined;
  return {
    ...(siteId ? { siteId } : {}),
    ...(import.meta.env.VITE_API_BASE_URL ? { apiBaseUrl: import.meta.env.VITE_API_BASE_URL } : {}),
    ...(firebase ? { firebase } : {}),
    ...injected,
  };
}
